import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Users, ArrowRight, Tag, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency } from '../contexts/CurrencyContext';
import AutoTranslatedText, { AutoTranslatedParagraph } from '../components/AutoTranslatedText';
import ReadMoreModal from '../components/ReadMoreModal';
import OptimizedImage from '../components/OptimizedImage';
import SEOHead from '../components/SEOHead';
import { parseLocalDate } from '../utils/dateUtils';
import { generateBreadcrumbSchema, SEO_CONFIG } from '../utils/seo';

// Region data with flags and names
const REGIONS = {
  us: { flag: '🇺🇸', name: 'United States' },
  ca: { flag: '🇨🇦', name: 'Canada' },
  fr: { flag: '🇫🇷', name: 'France' },
  de: { flag: '🇩🇪', name: 'Germany' },
  es: { flag: '🇪🇸', name: 'Spain' },
  gb: { flag: '🇬🇧', name: 'United Kingdom' },
  eu: { flag: '🇪🇺', name: 'Europe' },
  global: { flag: '🌍', name: 'Global' }
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [readMoreEvent, setReadMoreEvent] = useState(null);
  const [eventStats, setEventStats] = useState({});
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const currentLang = i18n.language || 'en';

  // Status priority order: live -> registration_open -> upcoming -> completed
  const statusPriority = {
    'live': 0,
    'registration_open': 1,
    'upcoming': 2,
    'completed': 3
  };

  // Sort events by status priority, then by start date
  const sortEvents = (eventsToSort) => {
    return [...eventsToSort].sort((a, b) => {
      // First, sort by status priority
      const priorityA = statusPriority[a.status] ?? 4;
      const priorityB = statusPriority[b.status] ?? 4;
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // If same status, sort by start date (ascending - nearest first)
      const dateA = parseLocalDate(a.start_date);
      const dateB = parseLocalDate(b.start_date);
      return dateA - dateB;
    });
  };

  useEffect(() => {
    fetchEvents();
  }, [filter]);

  const fetchEvents = async () => {
    try {
      const url = filter === 'all' ? `${API}/events` : `${API}/events?status=${filter}`;
      const response = await axios.get(url);
      // Sort events before setting state
      const sortedEvents = sortEvents(response.data);
      setEvents(sortedEvents);
      
      // Fetch registration stats for events that have show_registration_stats enabled
      const statsPromises = sortedEvents
        .filter(event => event.show_registration_stats)
        .map(event => 
          axios.get(`${API}/events/${event.id}/registration-stats`)
            .then(res => ({ eventId: event.id, stats: res.data }))
            .catch(() => null)
        );
      
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.filter(Boolean).forEach(({ eventId, stats }) => {
        statsMap[eventId] = stats;
      });
      setEventStats(statsMap);
    } catch (error) {
      console.error('Failed to fetch events', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      live: 'bg-accent/10 text-accent',
      registration_open: 'bg-emerald-100 text-emerald-700',
      upcoming: 'bg-primary/10 text-primary',
      completed: 'bg-slate-200 text-slate-700',
    };
    return styles[status] || styles.upcoming;
  };

  // Check if early bird discount is active for an event
  const isEarlyBirdActive = (event) => {
    if (!event.early_bird_enabled) return false;
    try {
      const startDate = parseLocalDate(event.start_date);
      const daysBeforeMs = (event.early_bird_days_before || 30) * 24 * 60 * 60 * 1000;
      const deadline = new Date(startDate.getTime() - daysBeforeMs);
      return new Date() < deadline;
    } catch {
      return false;
    }
  };

  const getEarlyBirdDeadline = (event) => {
    try {
      const startDate = parseLocalDate(event.start_date);
      const daysBeforeMs = (event.early_bird_days_before || 30) * 24 * 60 * 60 * 1000;
      return new Date(startDate.getTime() - daysBeforeMs);
    } catch {
      return null;
    }
  };

  const getFilterLabel = (status) => {
    const labels = {
      all: t('events.all'),
      live: t('events.live'),
      registration_open: t('events.open_registration'),
      upcoming: t('events.upcoming'),
      completed: t('events.completed'),
    };
    return labels[status] || status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Breadcrumb schema for search results
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Events' }
  ]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="events-page">
      <SEOHead 
        title="Virtual Trade Shows & Exhibitions | TradeHub24"
        description="Browse upcoming and live virtual tradeshows. Register as an exhibitor or attendee. Discover new products and connect with industry leaders."
        canonicalUrl={`${SEO_CONFIG.siteUrl}/events`}
        structuredData={breadcrumbSchema}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-3">{t('events.title')}</h1>
          <p className="text-lg text-slate-600">{t('events.subtitle')}</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8">
          {['all', 'live', 'registration_open', 'upcoming', 'completed'].map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              onClick={() => setFilter(status)}
              size="sm"
              data-testid={`filter-${status}-btn`}
            >
              {getFilterLabel(status)}
            </Button>
          ))}
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">{t('events.loading')}</p>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-md">
            <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">{t('events.no_events')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <div
                key={event.id}
                className="bg-white border border-slate-200 rounded-md overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`event-card-${event.id}`}
              >
                {/* Event Banner */}
                <div className="aspect-video bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center overflow-hidden relative">
                  {event.banner_url ? (
                    <OptimizedImage 
                      src={event.banner_url}
                      alt={event.name}
                      className="w-full h-full"
                      objectFit="cover"
                    />
                  ) : (
                    <Calendar className="h-12 w-12 text-primary" />
                  )}
                  {/* Region Badge */}
                  {event.region && REGIONS[event.region] && (
                    <div className="absolute top-2 left-2 bg-white/95 backdrop-blur-sm text-slate-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 shadow-md">
                      <span className="text-base">{REGIONS[event.region].flag}</span>
                      <span>{REGIONS[event.region].name}</span>
                    </div>
                  )}
                  {/* Early Bird Badge */}
                  {isEarlyBirdActive(event) && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg animate-pulse">
                      <Tag className="h-3 w-3" />
                      {event.early_bird_discount_percent}% {t('events.early_bird')}
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(event.status)}`}>
                        {event.status.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 font-mono">
                      {parseLocalDate(event.start_date).toLocaleDateString()}
                    </span>
                  </div>
                  
                  {/* Registration Stats Badge */}
                  {eventStats[event.id] && (eventStats[event.id].show_attendee_count || eventStats[event.id].show_exhibitor_count) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-3">
                      {eventStats[event.id].show_attendee_count && (
                        <div className="flex items-center gap-1 text-blue-800 text-xs font-medium">
                          <Users className="h-3 w-3" />
                          <span>{eventStats[event.id].attendee_count} {t('events.attendees')}</span>
                        </div>
                      )}
                      {eventStats[event.id].show_exhibitor_count && (
                        <div className="flex items-center gap-1 text-blue-800 text-xs font-medium">
                          <Building2 className="h-3 w-3" />
                          <span>{eventStats[event.id].exhibitor_count} {t('events.exhibitors')}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Early Bird Info Banner */}
                  {isEarlyBirdActive(event) && (
                    <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
                      <p className="text-green-800 text-xs font-medium">
                        🎉 {t('events.early_bird')}: {t('events.early_bird_save', { percent: event.early_bird_discount_percent, date: getEarlyBirdDeadline(event)?.toLocaleDateString() })}
                      </p>
                    </div>
                  )}
                  
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    <AutoTranslatedText
                      text={event.name}
                      translations={event.name_translations}
                      showLoading={false}
                    />
                  </h3>
                  <AutoTranslatedParagraph
                    text={event.description}
                    translations={event.description_translations}
                    maxLength={120}
                    className="text-sm mb-4"
                    onReadMore={() => setReadMoreEvent(event)}
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-500">
                      {isEarlyBirdActive(event) ? (
                        <span>
                          <span className="line-through text-slate-400">{formatPrice(event.buyer_fee, true)}</span>
                          <span className="ml-1 font-mono font-medium text-green-600">
                            {formatPrice(event.buyer_fee * (1 - event.early_bird_discount_percent / 100), true)}
                          </span> {t('events.entry_fee')}
                        </span>
                      ) : (
                        <span><span className="font-mono font-medium">{formatPrice(event.buyer_fee, true)}</span> {t('events.entry_fee')}</span>
                      )}
                    </div>
                    <Link to={`/events/${event.id}`}>
                      <Button size="sm" variant="outline" data-testid={`view-event-${event.id}-btn`}>
                        {t('events.view_details')} <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Read More Modal with Auto-Translation */}
      {readMoreEvent && (
        <ReadMoreModal
          open={!!readMoreEvent}
          onClose={() => setReadMoreEvent(null)}
          title={readMoreEvent.name}
          titleTranslations={readMoreEvent.name_translations}
          description={readMoreEvent.description}
          descriptionTranslations={readMoreEvent.description_translations}
          imageUrl={readMoreEvent.banner_url}
          autoTranslate={true}
        />
      )}
    </div>
  );
};

export default EventsPage;
